import {
	type MyTicketDTO,
	TICKET_KINDS,
	type TicketKind,
	type TicketStatus,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useCallback, useEffect, useMemo } from "react"
import { Link, useLocation, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { formatFullDateTime } from "../../lib/user-display"
import { PageContainer } from "../components/page-container"
import { PageHeader } from "../components/page-header"
import {
	TicketKindBadge,
	TicketStatusBadge,
} from "../components/tickets/ticket-card"
import { TICKET_BOARD_STATUSES } from "../hooks/ticket-api-paths"
import { useProjects } from "../hooks/use-projects"
import { useProperties } from "../hooks/use-properties"
import { useMyTickets } from "../hooks/use-tickets"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

interface MyTicketsFilters {
	propertyId?: string
	projectId?: string
	status?: TicketStatus
	kind?: TicketKind
}

export function MyTicketsPage() {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const [searchParams, setSearchParams] = useSearchParams()
	const location = useLocation()
	const filters = useMemo(
		() => parseMyTicketsFilters(searchParams),
		[searchParams],
	)
	const query = useMyTickets(filters)
	const properties = useProperties()
	const projects = useProjects(filters.propertyId)
	const tickets = query.data?.pages.flatMap((page) => page.tickets) ?? []
	const propertyNames = useMemo(() => {
		const map = new Map<string, string>()
		for (const property of properties.data ?? []) {
			map.set(property.id, property.name)
		}
		return map
	}, [properties.data])
	const projectNames = useMemo(() => {
		const map = new Map<string, string>()
		for (const project of projects.data ?? []) {
			map.set(project.id, project.name)
		}
		return map
	}, [projects.data])
	const updateFilters = useCallback(
		(patch: Partial<MyTicketsFilters>) => {
			const next = new URLSearchParams(searchParams)
			for (const [key, value] of Object.entries(patch)) {
				if (value) next.set(key, value)
				else next.delete(key)
			}
			if ("propertyId" in patch) next.delete("projectId")
			setSearchParams(next)
		},
		[searchParams, setSearchParams],
	)

	useEffect(() => {
		if (!filters.propertyId && filters.projectId) {
			updateFilters({ projectId: undefined })
			return
		}
		if (!filters.projectId || projects.isLoading) return
		if (!projectNames.has(filters.projectId)) {
			updateFilters({ projectId: undefined })
		}
	}, [
		filters.propertyId,
		filters.projectId,
		projects.isLoading,
		projectNames,
		updateFilters,
	])

	const hasFilters = Boolean(
		filters.propertyId || filters.projectId || filters.status || filters.kind,
	)

	const returnTo = {
		path: `${location.pathname}${location.search}`,
		label: t.tickets.backToMyTasks,
	}

	return (
		<div className="min-h-screen w-full bg-background">
			<PageContainer chrome="bare">
				<PageHeader title={t.tickets.myTicketsTitle} />

				<form
					aria-label={t.tickets.filtersLabel}
					className="mb-5 rounded-lg border border-border bg-card px-4 py-3"
				>
					<div className="flex flex-wrap items-end gap-3">
						<MyTicketsFilterSelect
							id="property"
							label={t.tickets.filterProperty}
							value={filters.propertyId ?? ""}
							onChange={(propertyId) =>
								updateFilters({ propertyId: propertyId || undefined })
							}
							options={[
								{ value: "", label: t.tickets.filterAllProperties },
								...(properties.data ?? []).map((property) => ({
									value: property.id,
									label: property.name,
								})),
							]}
						/>
						<MyTicketsFilterSelect
							id="project"
							label={t.tickets.filterProject}
							value={filters.projectId ?? ""}
							disabled={!filters.propertyId}
							onChange={(projectId) =>
								updateFilters({ projectId: projectId || undefined })
							}
							options={[
								{ value: "", label: t.tickets.filterAllProjects },
								...(projects.data ?? []).map((project) => ({
									value: project.id,
									label: project.name,
								})),
							]}
						/>
						<MyTicketsFilterSelect
							id="status"
							label={t.tickets.statusLabel}
							value={filters.status ?? ""}
							onChange={(status) =>
								updateFilters({
									status: (status || undefined) as TicketStatus | undefined,
								})
							}
							options={[
								{ value: "", label: t.tickets.myTicketsAllStatuses },
								...TICKET_BOARD_STATUSES.map((status) => ({
									value: status,
									label: t.tickets.status[status],
								})),
							]}
						/>
						<MyTicketsFilterSelect
							id="kind"
							label={t.tickets.filterKind}
							value={filters.kind ?? ""}
							onChange={(kind) =>
								updateFilters({
									kind: (kind || undefined) as TicketKind | undefined,
								})
							}
							options={[
								{ value: "", label: t.tickets.filterAllKinds },
								...TICKET_KINDS.map((kind) => ({
									value: kind,
									label: t.tickets.kind[kind],
								})),
							]}
						/>
						{hasFilters ? (
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => setSearchParams(new URLSearchParams())}
							>
								{t.tickets.filterClear}
							</Button>
						) : null}
					</div>
				</form>

				{query.isLoading ? (
					<div className="space-y-3">
						{["my-ticket-skeleton-1", "my-ticket-skeleton-2"].map((key) => (
							<div
								key={key}
								className="h-24 animate-pulse rounded-lg border border-border bg-card"
							/>
						))}
					</div>
				) : query.error ? (
					<div
						role="alert"
						className="rounded-lg border border-destructive bg-destructive px-4 py-3 text-sm text-destructive-foreground"
					>
						{query.error.message}
					</div>
				) : tickets.length === 0 ? (
					<div className="rounded-lg border border-dashed border-border bg-muted/30 px-6 py-10 text-center">
						<h2 className="text-xl font-bold text-foreground">
							{hasFilters
								? t.tickets.myTicketsFilteredEmptyTitle
								: t.tickets.myTicketsEmptyTitle}
						</h2>
						<p className="mt-2 text-sm text-muted-foreground">
							{hasFilters
								? t.tickets.myTicketsFilteredEmptyDescription
								: t.tickets.myTicketsEmptyDescription}
						</p>
						{hasFilters ? (
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="mt-4"
								onClick={() => setSearchParams(new URLSearchParams())}
							>
								{t.tickets.filterClear}
							</Button>
						) : null}
					</div>
				) : (
					<div className="space-y-3">
						{tickets.map((ticket) => (
							<MyTicketRow
								key={ticket.id}
								ticket={ticket}
								propertyName={
									ticket.propertyName || propertyNames.get(ticket.propertyId)
								}
								projectName={
									ticket.projectName ||
									(ticket.projectId ? projectNames.get(ticket.projectId) : null)
								}
								returnTo={returnTo}
							/>
						))}
					</div>
				)}

				{query.hasNextPage ? (
					<Button
						type="button"
						variant="outline"
						className="mt-4 w-full"
						disabled={query.isFetchingNextPage}
						onClick={() => {
							void query.fetchNextPage()
						}}
					>
						{query.isFetchingNextPage
							? t.tickets.loadingMore
							: t.tickets.loadMore}
					</Button>
				) : null}
			</PageContainer>
		</div>
	)
}

function MyTicketsFilterSelect({
	id,
	label,
	value,
	options,
	disabled = false,
	onChange,
}: {
	id: string
	label: string
	value: string
	options: { value: string; label: string }[]
	disabled?: boolean
	onChange: (value: string) => void
}) {
	const selectId = `my-ticket-filter-${id}`
	return (
		<label className="min-w-36 flex-1 text-xs font-medium text-muted-foreground sm:flex-none">
			<span className="mb-1 block">{label}</span>
			<select
				id={selectId}
				aria-label={label}
				value={value}
				disabled={disabled}
				onChange={(event) => onChange(event.target.value)}
				className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none focus:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60"
			>
				{options.map((option) => (
					<option key={option.value || "__all"} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</label>
	)
}

function MyTicketRow({
	ticket,
	propertyName,
	projectName,
	returnTo,
}: {
	ticket: MyTicketDTO
	propertyName: string | undefined
	projectName: string | null | undefined
	returnTo: { path: string; label: string }
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)

	return (
		<article
			data-testid="my-ticket-row"
			className="rounded-lg border border-border bg-card px-4 py-4 shadow-sm transition-colors hover:border-foreground/25"
		>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<Link
						to={webPaths.ticket(ticket.propertyId, ticket.id)}
						state={{ returnTo }}
						className="block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
					>
						<h2 className="font-semibold leading-snug text-foreground">
							{ticket.title}
						</h2>
					</Link>
					{ticket.description ? (
						<p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
							{ticket.description}
						</p>
					) : null}
				</div>
				<div className="flex shrink-0 flex-wrap justify-end gap-2">
					<TicketStatusBadge status={ticket.status} t={t.tickets} />
					<TicketKindBadge kind={ticket.kind} t={t.tickets} />
				</div>
			</div>
			<dl className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
				{propertyName ? (
					<div className="min-w-0">
						<dt className="sr-only">{t.tickets.filterProperty}</dt>
						<dd>
							<Link
								to={webPaths.property(ticket.propertyId)}
								className="rounded-sm border border-border bg-muted px-2 py-0.5 text-foreground hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
							>
								{t.tickets.filterProperty}: {propertyName}
							</Link>
						</dd>
					</div>
				) : null}
				{ticket.projectId && projectName ? (
					<div className="min-w-0">
						<dt className="sr-only">{t.tickets.filterProject}</dt>
						<dd>
							<Link
								to={webPaths.project(ticket.propertyId, ticket.projectId)}
								className="rounded-sm border border-border bg-muted px-2 py-0.5 text-foreground hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
							>
								{t.tickets.filterProject}: {projectName}
							</Link>
						</dd>
					</div>
				) : null}
				<div>
					<dt className="sr-only">{t.tickets.updatedLabel}</dt>
					<dd>{formatFullDateTime(ticket.updatedAt, locale)}</dd>
				</div>
			</dl>
		</article>
	)
}

function parseMyTicketsFilters(params: URLSearchParams): MyTicketsFilters {
	const status = params.get("status")
	const kind = params.get("kind")
	return {
		propertyId: params.get("propertyId") ?? undefined,
		projectId: params.get("projectId") ?? undefined,
		status:
			status && TICKET_BOARD_STATUSES.includes(status as TicketStatus)
				? (status as TicketStatus)
				: undefined,
		kind:
			kind && TICKET_KINDS.includes(kind as TicketKind)
				? (kind as TicketKind)
				: undefined,
	}
}
